import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations, NIMNY_AVATAR } from '../data/translations';
import { NightAmbience } from '../components/NimnyLanding';

const AboutPage = () => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  // Detect language from URL path and set it
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/ru/')) {
      setLanguage('ru');
    } else if (path.startsWith('/es/')) {
      setLanguage('es');
    } else {
      // Default to English for /about-nimny
      if (language !== 'en' && path === '/about-nimny') {
        // Keep current language if user navigated from another page
      }
    }
  }, [location.pathname, setLanguage, language]);

  // Update document title and meta tags based on language
  useEffect(() => {
    document.title = t(translations.aboutPage.metaTitle);
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t(translations.aboutPage.metaDescription));
    }

    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', t(translations.aboutPage.metaTitle));
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', t(translations.aboutPage.metaDescription));
    }

    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', t(translations.aboutPage.metaTitle));
    }
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', t(translations.aboutPage.metaDescription));
    }

    return () => {
      // Reset to homepage defaults when leaving
      document.title = 'Nimny – Bedtime Stories for Children | Calm Night Stories to Read Aloud';
    };
  }, [language, t]);

  // Note: FAQ has been MOVED to ContactPage (App Store Support URL).
  // FAQ data still lives in translations.aboutPage.faq as the single
  // source of truth — only the rendering location changed.

  // Add Breadcrumb structured data and canonical tag
  useEffect(() => {
    const homeUrl = language === 'en' ? 'https://readnimny.com/' : `https://readnimny.com/${language === 'ru' ? 'ru' : 'es'}`;
    const aboutUrl = language === 'en' ? 'https://readnimny.com/about-nimny' : `https://readnimny.com/${language === 'ru' ? 'ru' : 'es'}/about-nimny`;
    
    const homeName = language === 'ru' ? 'Главная' : language === 'es' ? 'Inicio' : 'Home';
    const aboutName = language === 'ru' ? 'О Нимни' : language === 'es' ? 'Sobre Nimny' : 'About Nimny';

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": homeName,
          "item": homeUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": aboutName,
          "item": aboutUrl
        }
      ]
    };

    // Remove existing breadcrumb schema if present
    const existingBreadcrumbSchema = document.getElementById('breadcrumb-schema');
    if (existingBreadcrumbSchema) {
      existingBreadcrumbSchema.remove();
    }

    // Add Breadcrumb schema
    const script = document.createElement('script');
    script.id = 'breadcrumb-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script);

    // Add/Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = aboutUrl;

    return () => {
      const schema = document.getElementById('breadcrumb-schema');
      if (schema) {
        schema.remove();
      }
    };
  }, [language]);

  // Helper to get array translations
  const tArray = (obj) => obj[language] || obj['en'] || [];

  return (
    <div className="min-h-screen relative">
      {/* Night sky background */}
      <NightAmbience />
      
      {/* Simple header with back button only */}
      <header className="relative z-10 py-6 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-amber-200/70 hover:text-amber-200 transition-colors font-sans"
            data-testid="about-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{language === 'ru' ? 'На главную' : language === 'es' ? 'Volver al inicio' : 'Back to home'}</span>
          </Link>
        </div>
      </header>
      
      <main className="relative pb-16 px-4 md:px-6">
        <article className="max-w-3xl mx-auto">
          {/* Hero Banner Image */}
          <div className="mb-8 md:mb-10 -mx-4 md:mx-0">
            <div className="relative overflow-hidden rounded-none md:rounded-2xl">
              <img 
                src="/nimny-about-header.jpg"
                alt="Nimny - Gentle bedtime story"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Page Title */}
          <h1 
            className="text-3xl md:text-4xl text-[#c9a87c] mb-8 md:mb-10"
            style={{ fontFamily: '"Patrick Hand", cursive' }}
          >
            {t(translations.aboutPage.title)}
          </h1>

          {/* Introduction */}
          <section className="mb-10">
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-5">
              {t(translations.aboutPage.intro1)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed">
              {t(translations.aboutPage.intro2)}
            </p>
          </section>

          {/* What you can do now */}
          <section className="mb-10">
            <h2 
              className="text-xl md:text-2xl text-[#c9a87c] mb-5"
              style={{ fontFamily: '"Patrick Hand", cursive' }}
            >
              {t(translations.aboutPage.whatYouCanDoTitle)}
            </h2>
            <ul className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed list-disc list-inside space-y-2 pl-2">
              {tArray(translations.aboutPage.whatYouCanDoList).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Who is Nimny */}
          <section className="mb-10">
            <h2 
              className="text-xl md:text-2xl text-[#c9a87c] mb-5"
              style={{ fontFamily: '"Patrick Hand", cursive' }}
            >
              {t(translations.aboutPage.whoIsNimnyTitle)}
            </h2>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-5">
              {t(translations.aboutPage.whoIsNimny1)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-5">
              {t(translations.aboutPage.whoIsNimny2)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-3">
              {t(translations.aboutPage.sometimesIntro)}
            </p>
            <div className="font-serif text-[#F4E6C8]/80 text-base md:text-lg leading-relaxed italic pl-4 mb-5">
              {tArray(translations.aboutPage.sometimesList).map((item, i) => (
                <p key={i}>{item}</p>
              ))}
            </div>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-5">
              {t(translations.aboutPage.sometimesClosing)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed">
              {t(translations.aboutPage.personalStory)}
            </p>
          </section>

          {/* What makes Nimny different */}
          <section className="mb-10">
            <h2 
              className="text-xl md:text-2xl text-[#c9a87c] mb-5"
              style={{ fontFamily: '"Patrick Hand", cursive' }}
            >
              {t(translations.aboutPage.differentTitle)}
            </h2>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-3">
              {t(translations.aboutPage.different1)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-5 font-medium">
              {t(translations.aboutPage.different2)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-5">
              {t(translations.aboutPage.different3)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-3">
              {t(translations.aboutPage.focusIntro)}
            </p>
            <ul className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed list-disc list-inside space-y-1 pl-2">
              {tArray(translations.aboutPage.focusList).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Why bedtime rituals matter */}
          <section className="mb-10">
            <h2 
              className="text-xl md:text-2xl text-[#c9a87c] mb-5"
              style={{ fontFamily: '"Patrick Hand", cursive' }}
            >
              {t(translations.aboutPage.ritualsTitle)}
            </h2>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-3">
              {t(translations.aboutPage.rituals1)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-5">
              {t(translations.aboutPage.rituals2)}
            </p>
            <div className="font-serif text-[#F4E6C8]/80 text-base md:text-lg leading-relaxed italic pl-4 mb-5">
              {tArray(translations.aboutPage.ritualsPoetic).map((item, i) => (
                <p key={i}>{item}</p>
              ))}
            </div>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-3">
              {t(translations.aboutPage.ritualsClosingIntro)}
            </p>
            <div className="font-serif text-[#F4E6C8]/80 text-base md:text-lg leading-relaxed italic pl-4">
              {tArray(translations.aboutPage.ritualsClosingPoetic).map((item, i) => (
                <p key={i}>{item}</p>
              ))}
            </div>
          </section>

          {/* How the stories are structured */}
          <section className="mb-10">
            <h2 
              className="text-xl md:text-2xl text-[#c9a87c] mb-5"
              style={{ fontFamily: '"Patrick Hand", cursive' }}
            >
              {t(translations.aboutPage.structureTitle)}
            </h2>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-3">
              {t(translations.aboutPage.structure1)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-5 font-medium">
              {t(translations.aboutPage.structure2)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-5">
              {t(translations.aboutPage.structure3)}
            </p>
            <p className="font-serif text-[#F4E6C8]/80 text-base md:text-lg leading-relaxed italic whitespace-pre-line mb-5">
              {t(translations.aboutPage.structure4)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed">
              {t(translations.aboutPage.structureMyStories)}
            </p>
          </section>

          {/* Who Nimny is for */}
          <section className="mb-10">
            <h2 
              className="text-xl md:text-2xl text-[#c9a87c] mb-5"
              style={{ fontFamily: '"Patrick Hand", cursive' }}
            >
              {t(translations.aboutPage.forWhomTitle)}
            </h2>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-4">
              {t(translations.aboutPage.forWhomIntro)}
            </p>
            <ul className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed list-disc list-inside space-y-1 pl-2 mb-5">
              {tArray(translations.aboutPage.forWhomList).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed">
              {t(translations.aboutPage.forWhomClosing)}
            </p>
          </section>

          {/* A quiet promise */}
          <section>
            <h2 
              className="text-xl md:text-2xl text-[#c9a87c] mb-5"
              style={{ fontFamily: '"Patrick Hand", cursive' }}
            >
              {t(translations.aboutPage.promiseTitle)}
            </h2>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-3">
              {t(translations.aboutPage.promise1)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed mb-5 whitespace-pre-line">
              {t(translations.aboutPage.promise2)}
            </p>
            <p className="font-serif text-[#F4E6C8] text-base md:text-lg leading-relaxed font-medium">
              {t(translations.aboutPage.promise3)}
            </p>
          </section>
        </article>
      </main>
    </div>
  );
};

export default AboutPage;
